import { call, put, select, takeLatest } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import { showToastAction } from '../ui';
import {
    fetchList,
    listFetched,
    listFetchFailed,
    saveEdits,
    saveEditsSucceeded,
    saveEditsFailed,
    generateReport,
    generateReportSettled,
} from '../weeklyUnitNumbers';

const buildQuery = filters => {
    const out = {};
    if (filters.from != null) out.from = filters.from;
    if (filters.to != null) out.to = filters.to;
    if (filters.entityId) out.entityId = filters.entityId;
    if (filters.search) out.search = filters.search;
    return out;
};

function* fetchListEffect() {
    try {
        const filters = yield select(s => s.weeklyUnitNumbers.filters);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.weeklyUnitNumbers,
            method: 'GET',
            queryParams: buildQuery(filters),
        });
        yield put(listFetched(response));
    } catch (e) {
        yield put(listFetchFailed(e.message));
    }
}

function* saveEditsEffect() {
    try {
        const edits = yield select(s => s.weeklyUnitNumbers.edits);
        const items = Object.keys(edits).map(jobId => ({
            jobId,
            unitNumber: edits[jobId],
        }));

        if (!items.length) {
            yield put(saveEditsSucceeded({}));
            return;
        }

        const result = yield call(SendApiRequest, {
            url: apiRoutes.weeklyUnitNumbers,
            method: 'PUT',
            body: items,
        });

        const savedMap = {};
        items.forEach(i => {
            savedMap[i.jobId] = i.unitNumber;
        });

        yield put(saveEditsSucceeded(savedMap));

        const failedCount =
            result && Array.isArray(result.errors) ? result.errors.length : 0;
        const updatedCount =
            result && typeof result.updated === 'number'
                ? result.updated
                : items.length;

        yield put(
            showToastAction({
                type: failedCount ? 'warning' : 'success',
                message: failedCount
                    ? `Updated ${updatedCount} unit number(s), ${failedCount} failed.`
                    : `Updated ${updatedCount} unit number(s).`,
            }),
        );

        // Refresh so rows reflect the persisted state.
        yield put(fetchList());
    } catch (e) {
        yield put(saveEditsFailed(e.message));
        yield put(
            showToastAction({
                type: 'error',
                message: e.message || 'Failed to save unit numbers',
            }),
        );
    }
}

function* generateReportEffect() {
    try {
        // No date params — the backend defaults to the standard weekly window
        // (now -8d .. now -2d, America/Toronto), matching the existing process.
        const response = yield call(SendApiRequest, {
            url: apiRoutes.weeklyTransactionReport,
            method: 'POST',
        });
        yield put(
            showToastAction({
                type: 'success',
                message:
                    (response && response.message) ||
                    'Report generation started. The CSV will be uploaded to Dropbox shortly.',
            }),
        );
    } catch (e) {
        yield put(
            showToastAction({
                type: 'error',
                message: e.message || 'Failed to start report generation',
            }),
        );
    } finally {
        yield put(generateReportSettled());
    }
}

export const weeklyUnitNumbersSagas = [
    takeLatest(fetchList.type, fetchListEffect),
    takeLatest(saveEdits.type, saveEditsEffect),
    takeLatest(generateReport.type, generateReportEffect),
];
